import {
  getPointsRuleAttention,
  type ExpertReportResponse,
  type PointsRuleAttention,
  type PointsRuleOwner,
} from '@/entities/expert-report';
import type { KmAssessmentResponse, KmStructureGroupNode, KmStructureResponse } from '../model/api/types';

/**
 * Владельцы правила расчёта на модели знаний — само заключение и каждая группа структуры;
 * единица оценивания правила не несёт. Вложенные элементы владельца, по результатам которых
 * считается его результат, — вложенные в него группы и стоящие в нём единицы оценивания.
 */
const nestedNames = (groups: KmStructureGroupNode[], assessments: KmAssessmentResponse[]): string[] => [
  ...groups.map((group) => group.name),
  ...assessments.map((assessment) => assessment.name),
];

/** Группа структуры как владелец правила расчёта своего результата */
export const kmGroupAsPointsRuleOwner = (group: KmStructureGroupNode): PointsRuleOwner => ({
  name: group.name,
  pointsRule: group.pointsRule,
  childNames: nestedNames(group.children, group.assessments),
});

/**
 * Заключение на модели знаний как владелец правила расчёта своего итога: правило считает итог
 * по группам верхнего уровня и по единицам оценивания, стоящим рядом с ними.
 */
export const kmReportAsPointsRuleOwner = (
  report: ExpertReportResponse,
  structure: KmStructureResponse,
): PointsRuleOwner => ({
  name: report.name,
  pointsRule: report.pointsRule,
  childNames: nestedNames(structure.groups, structure.assessments),
});

/** Группа структуры по идентификатору; `null` — такой группы в структуре нет */
const findKmGroup = (groups: KmStructureGroupNode[], groupId: number): KmStructureGroupNode | null => {
  for (const group of groups) {
    if (group.id === groupId) return group;
    const found = findKmGroup(group.children, groupId);
    if (found) return found;
  }
  return null;
};

/**
 * Владелец правила расчёта, в которое входит группа: вышестоящая группа либо само заключение
 * (у группы верхнего уровня). Именно это правило нужно проверить, когда группа изменилась.
 */
export const findKmPointsRuleOwner = (
  report: ExpertReportResponse,
  structure: KmStructureResponse,
  parentGroupId: number | null,
): PointsRuleOwner => {
  if (parentGroupId === null) return kmReportAsPointsRuleOwner(report, structure);
  const parent = findKmGroup(structure.groups, parentGroupId);
  return parent ? kmGroupAsPointsRuleOwner(parent) : kmReportAsPointsRuleOwner(report, structure);
};

/**
 * Требует ли внимания правило расчёта хотя бы одной группы внутри поддерева; `null` — не требует.
 *
 * Нужна свёрнутому узлу: пока он не раскрыт, отметки самих групп не видны. Неприменимое правило
 * важнее считающего результат не по всему составу, поэтому при обеих причинах называется оно.
 */
export const findNestedKmPointsRuleAttention = (groups: KmStructureGroupNode[]): PointsRuleAttention | null => {
  let found: PointsRuleAttention | null = null;

  for (const group of groups) {
    const own = getPointsRuleAttention(kmGroupAsPointsRuleOwner(group));
    if (own === 'inapplicable') return own;

    const nested = findNestedKmPointsRuleAttention(group.children);
    if (nested === 'inapplicable') return nested;

    found = found ?? own ?? nested;
  }

  return found;
};
